// contexts/voice/VoiceProvider.tsx
// Main voice context provider that composes all hooks

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useTheme } from '../ThemeContext';
import { useLanguage } from '../LanguageContext';
import { VoiceContextType, GeminiVoiceId, VadMode, ChatMode } from './types';
import { VOICE_CONSTANTS } from './constants';
import { useVoiceMessages } from './useVoiceMessages';
import { useVoiceNotifications } from './useVoiceNotifications';
import { useVoiceUIActions } from './useVoiceUIActions';
import { useVoiceConnection } from './useVoiceConnection';

const VoiceContext = createContext<VoiceContextType | null>(null);

export function useVoice() {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
}

interface VoiceProviderProps {
  children: ReactNode;
}

export function VoiceProvider({ children }: VoiceProviderProps) {
  // Get theme and language context
  const { themeId, actAsUser } = useTheme();
  const { language } = useLanguage();

  // Voice selection (persisted in localStorage)
  const [selectedVoice, setSelectedVoiceState] = useState<GeminiVoiceId>(() => {
    const saved = localStorage.getItem(VOICE_CONSTANTS.STORAGE_KEY_VOICE);
    return (saved as GeminiVoiceId) || 'Aoede';
  });

  const setSelectedVoice = useCallback((voice: GeminiVoiceId) => {
    setSelectedVoiceState(voice);
    localStorage.setItem(VOICE_CONSTANTS.STORAGE_KEY_VOICE, voice);
  }, []);

  // VAD mode selection (persisted in localStorage, admin only)
  const [vadMode, setVadModeState] = useState<VadMode>(() => {
    const saved = localStorage.getItem(VOICE_CONSTANTS.STORAGE_KEY_VAD_MODE);
    if (saved === 'standard' || saved === 'gl31') return saved;
    return 'standard';
  });

  const setVadMode = useCallback((mode: VadMode) => {
    setVadModeState(mode);
    localStorage.setItem(VOICE_CONSTANTS.STORAGE_KEY_VAD_MODE, mode);
  }, []);

  // Message management
  const {
    messages,
    currentUserText,
    currentAssistantText,
    isTyping,
    appendUserText,
    appendAssistantText,
    addToolCall,
    completeTurn,
    resetMessages,
    setIsTyping,
  } = useVoiceMessages();

  // Notification management
  const {
    trackingData,
    pickupConfirmation,
    callbackConfirmation,
    handoverData,
    currentAgent,
    showTracking,
    dismissTracking,
    showPickupConfirmation,
    dismissPickupConfirmation,
    showCallbackConfirmation,
    dismissCallbackConfirmation,
    initiateHandover,
    dismissHandover,
    changeAgent,
    resetNotifications,
  } = useVoiceNotifications();

  // UI action handling
  const {
    highlightedProductIds,
    selectedProduct,
    handleUIAction,
    clearHighlights,
    setSelectedProduct,
    nextProductImage,
    previousProductImage,
  } = useVoiceUIActions({
    onShowTracking: showTracking,
    onShowPickupConfirmation: showPickupConfirmation,
    onShowCallbackConfirmation: showCallbackConfirmation,
    onInitiateHandover: initiateHandover,
    onChangeAgent: changeAgent,
  });

  // Connection management
  const {
    state,
    chatMode,
    error,
    muted,
    pushToTalkMode,
    audioDetectedWhileMuted,
    connect: connectionConnect,
    disconnect: connectionDisconnect,
    closeChat: connectionCloseChat,
    sendContext,
    sendTextMessage,
    toggleMute,
    setPushToTalkMode,
    startTalking,
    stopTalking,
  } = useVoiceConnection({
    themeId,
    language,
    selectedVoice,
    vadMode,
    actAsUser,
    onTranscript: appendUserText,
    onResponse: appendAssistantText,
    onUIAction: handleUIAction,
    onToolCall: addToolCall,
    onTurnComplete: completeTurn,
    onTyping: setIsTyping,
    onError: () => {}, // Error is handled via state
  });

  // Wrapped connect that resets messages
  const connect = useCallback(async (mode: ChatMode = 'voice') => {
    resetMessages();
    await connectionConnect(mode);
  }, [connectionConnect, resetMessages]);

  // Wrapped disconnect that resets notifications
  const disconnect = useCallback(() => {
    connectionDisconnect();
    resetNotifications();
  }, [connectionDisconnect, resetNotifications]);

  // Wrapped closeChat that resets messages for new session
  const closeChat = useCallback(() => {
    connectionCloseChat();
    resetMessages();
  }, [connectionCloseChat, resetMessages]);

  return (
    <VoiceContext.Provider value={{
      // Connection state
      state,
      chatMode,
      error,

      // Messages
      messages,
      currentUserText,
      currentAssistantText,
      isTyping,

      // Product highlighting
      highlightedProductIds,
      selectedProduct,

      // Audio controls
      muted,
      pushToTalkMode,
      audioDetectedWhileMuted,

      // Voice selection
      selectedVoice,
      setSelectedVoice,

      // VAD mode
      vadMode,
      setVadMode,

      // Notifications
      trackingData,
      pickupConfirmation,
      callbackConfirmation,
      handoverData,
      currentAgent,

      // Actions
      connect,
      disconnect,
      closeChat,
      clearHighlights,
      sendContext,
      sendTextMessage,
      toggleMute,
      setPushToTalkMode,
      startTalking,
      stopTalking,
      dismissTracking,
      dismissPickupConfirmation,
      dismissCallbackConfirmation,
      dismissHandover,
      setSelectedProduct,
      nextProductImage,
      previousProductImage,
    }}>
      {children}
    </VoiceContext.Provider>
  );
}
